import React from 'react';
import { Link } from 'react-router-dom';
import { FiBook, FiShoppingCart, FiCheck, FiTag, FiClock, FiTrendingUp } from 'react-icons/fi';

const CourseCard = ({ 
  course, 
  isPurchased = false,
  showPrice = true,
  showEnrollButton = true 
}) => {
  const finalPrice = course.price ? (course.price - (course.price * (course.discount || 0) / 100)).toFixed(2) : '0.00';

  // Helper function to strip HTML tags
  const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  return (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all overflow-hidden active:scale-95">
      {/* Course Thumbnail */}
      <Link to={`/course-details/${course.id}`} className="block relative overflow-hidden">
        <div className="relative h-32 sm:h-40 lg:h-44 overflow-hidden bg-gradient-to-br from-blue-100 to-purple-100 rounded-t-xl sm:rounded-t-2xl">
          <img
            src={course.course_thumbnail}
            alt={course.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            onError={(e) => {
              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%236366f1" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="white" font-size="24"%3ECourse%3C/text%3E%3C/svg%3E';
            }}
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

          {/* Discount Badge */}
          {course.discount > 0 && showPrice && (
            <div className="absolute top-2 left-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-2 py-1 rounded-full text-[10px] sm:text-xs font-bold shadow-lg flex items-center gap-1">
              <FiTrendingUp className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
              {course.discount}% OFF
            </div>
          )}

          {/* Purchase Status Badge */}
          {isPurchased && (
            <div className="absolute top-2 right-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-bold flex items-center gap-1 shadow-lg">
              <FiCheck className="w-3 h-3" />
              OWNED
            </div>
          )}
        </div>
      </Link>

      {/* Course Info */}
      <div className="p-3 sm:p-4">
        <Link to={`/course-details/${course.id}`}>
          <h3 className="text-sm sm:text-base lg:text-lg font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2 leading-tight">
            {course.title}
          </h3>
        </Link>
        
        {course.description && (
          <p className="text-[10px] sm:text-xs text-gray-600 mb-2 line-clamp-2 leading-relaxed hidden sm:block">
            {stripHtml(course.description)}
          </p>
        )}

        {/* Meta Info */}
        <div className="flex items-center justify-between mb-3 gap-1 sm:gap-2">
          <span className="text-[10px] sm:text-xs bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full font-semibold flex items-center gap-1 border border-blue-100">
            <FiTag className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
            <span className="truncate max-w-[60px] sm:max-w-[80px]">{course.tradeName || course.tradegroupName || 'General'}</span>
          </span>
          {course.validity && (
            <span className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-1 bg-gray-50 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full">
              <FiClock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
              {course.validity} M
            </span>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100 my-2 sm:my-3"></div>

        {/* Price Section */}
        {showPrice && !isPurchased && (
          <div className="mb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-1 sm:gap-2">
                <span className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-600">
                  ₹{finalPrice}
                </span>
                {course.discount > 0 && (
                  <span className="text-[10px] sm:text-xs text-gray-400 line-through">
                    ₹{course.price.toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {isPurchased ? (
          <Link
            to={`/course/${course.id}`}
            state={{ course }}
            className="w-full flex items-center justify-center gap-1 sm:gap-2 px-3 py-2 sm:py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-b-xl sm:rounded-b-2xl font-bold text-xs sm:text-sm transition-all hover:shadow-lg active:scale-95"
          >
            <FiBook className="w-3 h-3 sm:w-4 sm:h-4" />
            Open Course
          </Link>
        ) : showEnrollButton ? (
          <div className="flex flex-col gap-1.5 sm:gap-2">
            {/* Enroll Now Button */}
            <Link
              to={`/course-details/${course.id}`}
              className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-bold text-xs sm:text-sm transition-all hover:shadow-lg active:scale-95"
            >
              <FiShoppingCart className="w-3 h-3 sm:w-4 sm:h-4" />
              Enroll Now
            </Link>
            
            {/* View Course Button */}
            <Link
              to={`/course/${course.id}`}
              state={{ course }}
              className="w-full flex items-center justify-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg font-semibold text-[10px] sm:text-xs transition-all hover:bg-blue-100 active:scale-95"
            >
              <FiBook className="w-3 h-3" />
              View Testseries
            </Link>
          </div>
        ) : (
          <Link
            to={`/course/${course.id}`}
            state={{ course }}
            className="w-full flex items-center justify-center gap-1 sm:gap-2 px-3 py-2 sm:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-xs sm:text-sm transition-all"
          >
            <FiBook className="w-3 h-3 sm:w-4 sm:h-4" />
            View Course
          </Link>
        )}
      </div>
    </div>
  );
};

export default CourseCard;
